import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import Header from '../../components/Header';
import { authGet } from '../../lib/api';
import { formatDateTime } from '../../lib/dateFormat';

function prettyJson(value) {
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function formatTimestamp(value) {
  return formatDateTime(value);
}

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await authGet('/api/admin/audit-logs');
      setLogs(data.auditLogs || []);
    } catch (err) {
      setError(err.message || 'Unable to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const rows = useMemo(() => logs.map((log) => ({
    ...log,
    actor: log.user_name || log.user_email || log.user_id || 'Unknown user',
    timestamp: formatTimestamp(log.created_at),
    object: [log.object_type, log.object_id].filter(Boolean).join(' / '),
    before: prettyJson(log.before_value),
    after: prettyJson(log.after_value)
  })), [logs]);

  const columns = [
    { field: 'timestamp', headerName: 'Timestamp', minWidth: 180, flex: 0.8 },
    { field: 'actor', headerName: 'User', minWidth: 180, flex: 0.9 },
    {
      field: 'action',
      headerName: 'Action',
      minWidth: 150,
      flex: 0.7,
      renderCell: (params) => <Chip size="small" label={params.value} />
    },
    { field: 'object', headerName: 'Object', minWidth: 220, flex: 1 },
    {
      field: 'before',
      headerName: 'Before Value',
      minWidth: 320,
      flex: 1.3,
      renderCell: (params) => (
        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: 12, m: 0, py: 1 }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'after',
      headerName: 'After Value',
      minWidth: 320,
      flex: 1.3,
      renderCell: (params) => (
        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: 12, m: 0, py: 1 }}>
          {params.value}
        </Box>
      )
    }
  ];

  return (
    <Box m="20px">
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
        <Header title="Audit Logs" subtitle="Admin-only review of changes from the previous 7 days" />
        <Button variant="outlined" onClick={loadLogs} disabled={loading}>Refresh</Button>
      </Stack>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Stack spacing={2}>
          <Alert severity="info">
            Audit logs are retained for 7 days and include user, timestamp, action, object, before value, and after value.
          </Alert>

          {error && <Alert severity="error">{error}</Alert>}

          {loading ? (
            <Stack alignItems="center" py={6}>
              <CircularProgress />
            </Stack>
          ) : (
            <Box sx={{ height: 680, width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                getRowHeight={() => 'auto'}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } }
                }}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
              />
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
